import { moneyTotals } from '../format';
import type { BrandName, Summary } from '../types';

/**
 * The eight-tile KPI grid from the command centre mock.
 *
 * The mock carries a number in every tile. Six of the eight have no
 * source in this system, so they render as empty tiles that SAY why
 * (Matthew, 4 September 2026: "an empty tile that says why is the
 * point"). None is hidden, and none is computed from a placeholder: a
 * dashboard that invents a runway figure is worse than one that admits
 * it has no financial source, because the invented one gets quoted.
 *
 * Pipeline and Enterprise clients were to render if organisations/deals
 * had rows. Checked against production on 4 September 2026: both tables
 * exist and both are empty, and shell_read holds no SELECT on either, so
 * they join the unconnected six. When the CRM read model lands, they
 * become real by deleting their reason strings and reading the tables.
 */
type Tile =
  | { label: string; value: string; delta?: string }
  | { label: string; reason: string };

function isEmpty(t: Tile): t is { label: string; reason: string } {
  return 'reason' in t;
}

export function KpiGrid({
  summary,
  brands,
}: {
  summary: Summary | null;
  brands: BrandName[];
}) {
  // Revenue month to date, summed across the brands this role can see.
  // moneyTotals renders every currency present rather than blending
  // them, which is the same rule the group tiles already follow.
  const mtdRevenue = summary
    ? brands.reduce<Record<string, number>>((acc, b) => {
        const rev = summary.mtd[b]?.revenue;
        if (rev) for (const [ccy, amt] of Object.entries(rev)) acc[ccy] = (acc[ccy] ?? 0) + amt;
        return acc;
      }, {})
    : null;
  // "Active" means the brand is instrumented AND has moved this month:
  // an instrumented brand with no activity is connected, not active.
  const activeBrands = summary
    ? brands.filter((b) => {
        const t = summary.mtd[b];
        return Boolean(t?.instrumented) && ((t?.clicks ?? 0) > 0 || (t?.transactionCount ?? 0) > 0);
      }).length
    : null;

  const revenueHidden = summary ? brands.every((b) => summary.mtd[b]?.revenue === null) : false;

  const tiles: Tile[] = [
    revenueHidden
      ? { label: 'Revenue (MTD)', reason: 'revenue is hidden for your role' }
      : {
          label: 'Revenue (MTD)',
          value: mtdRevenue === null ? '…' : moneyTotals(mtdRevenue),
        },
    { label: 'Active brands', value: activeBrands === null ? '…' : String(activeBrands) },
    { label: 'Pipeline (weighted)', reason: 'no CRM source connected' },
    { label: 'Enterprise clients', reason: 'no CRM source connected' },
    { label: 'Cash', reason: 'no financial source connected' },
    { label: 'Runway', reason: 'no financial source connected' },
    { label: 'Impact generated', reason: 'impact not yet defined' },
    { label: 'Addaiaspora health', reason: 'health score not yet defined' },
  ];

  return (
    <div className="cc-kpi-grid">
      {tiles.map((t) => (
        <div key={t.label} className={`cc-kpi${isEmpty(t) ? ' cc-kpi-empty' : ''}`}>
          <div className="label">{t.label}</div>
          {isEmpty(t) ? (
            <>
              <div className="value">—</div>
              <div className="reason">{t.reason}</div>
            </>
          ) : (
            <div className="value">{t.value}</div>
          )}
        </div>
      ))}
    </div>
  );
}
